import { Component, LOCALE_ID, OnDestroy, OnInit } from '@angular/core';
import { CommonModule, DatePipe, Location, NgIf, registerLocaleData } from '@angular/common';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { MatIconModule } from '@angular/material/icon';
import { MatSnackBar, MatSnackBarRef } from '@angular/material/snack-bar';
import { take } from 'rxjs/operators';

import localeEl from '@angular/common/locales/el';

import { CoreService } from '../../../services/core.service';
import { environment } from '../../../../environments/environment';

import { ManagePreviewActionButtonComponent } from '../components/manage-preview-action-button/manage-preview-action-button.component';
import { ManagePreviewLatestAlertsComponent } from '../components/manage-preview-latest-alerts/manage-preview-latest-alerts.component';
import { ManagePreviewLatestPostsComponent } from '../components/manage-preview-latest-posts/manage-preview-latest-posts.component';

registerLocaleData(localeEl);

type TextDirection = 'ltr' | 'rtl';

interface InstagramEditPostResponse {
  Domain?: string;
  text_direction?: TextDirection | string;
  post_status?: string;
  social_tag?: string;

  feed?: string;
  thumbnail_file_path?: string;
  rss_title?: string;
  rss_description?: string;
  article_url?: string;
  scheduled_for?: string;

  rss_img_url?: string;
  switchboard_img?: string | null;
  image_array?: string[];

  createomate_video?: string | null;
  video_url?: string | null;
}

@Component({
  selector: 'app-instagram-photopost',
  standalone: true,
  imports: [
    CommonModule,
    NgIf,
    MatIconModule,
    RouterLink,
    ManagePreviewActionButtonComponent,
    ManagePreviewLatestAlertsComponent,
    ManagePreviewLatestPostsComponent
  ],
  providers: [
    DatePipe,
    { provide: LOCALE_ID, useValue: 'el' }
  ],
  templateUrl: './instagram-photopost.component.html',
  styleUrl: './instagram-photopost.component.scss'
})
export class InstagramPhotopostComponent implements OnInit, OnDestroy {
  postId: string | null = null;
  companyId = 0;

  loading = true;
  errorMessage = '';

  siteDomain = '';
  postFeed = '';
  thumbnailFilePath = '';
  postTitle = '';
  postDescription = '';
  articleUrl = '';
  formattedDate = '';
  postStatus = '';
  socialTag = '';
  textDirection: TextDirection = 'ltr';

  mainImageUrl = '';
  videoUrl = '';

  isVideo = false;
  isCarousel = false;
  isSwitchboardImage = false;

  carouselImages: string[] = [];
  currentIndex = 0;

  latestPosts = [
    {
      id: 14364,
      title: 'Ζώδια Παρασκευής 22 Μαΐου 2026',
      image: 'https://www.allyou.gr/images/zodia-paraskevis-maiou-allyou-bg.jpg#joomlaImage://local-images/zodia-paraskevis-maiou-allyou-bg.jpg',
      domain: 'allyou.gr',
      platform: 'Instagram'
    },
    {
      id: 14365,
      title: 'Γάμος Taylor Swift και Travis Kelce: φήμες για ημερομηνία, τοποθεσία, καλεσμένους και όλα τα πιθανά σενάρια',
      image: 'https://www.allyou.gr/images/taylor-swift-travis-kelce-wedding-cov.jpg#joomlaImage://local-images/taylor-swift-travis-kelce-wedding-cov.jpg',
      domain: 'allyou.gr',
      platform: 'Instagram'
    },
    {
      id: 14366,
      title: 'Η Pharmakon έρχεται στην Αθήνα για μια βραδιά ηχητικής αποδόμησης',
      image: 'https://www.avopolis.gr/images/Giannis%20Papaioannou/FH010001_011.jpg',
      domain: 'avopolis.gr',
      platform: 'Instagram'
    }
  ];

  latestAlerts = [
    {
      id: 14362,
      title: 'Τύποι ιδιοσυγκρασίας παιδιού: τι σημαίνουν και πώς επηρεάζουν τη σχέση γονέα-παιδιού',
      image: 'https://childit.gr/wp-content/uploads/2026/05/idiosigrasia-paidiou-kai-goneikotita-childit-b.jpg',
      domain: 'childit.gr',
      platform: 'Instagram',
      alertTitle: 'Spike in Clicks',
      alertValue: '+86.11% (36 → 67)',
      alertType: 'positive'
    },
    {
      id: 14364,
      title: 'Ζώδια Παρασκευής 22 Μαΐου 2026',
      image: 'https://www.allyou.gr/images/zodia-paraskevis-maiou-allyou-bg.jpg#joomlaImage://local-images/zodia-paraskevis-maiou-allyou-bg.jpg',
      domain: 'allyou.gr',
      platform: 'Instagram',
      alertTitle: 'Spike in Clicks',
      alertValue: '+6314.29% (7 → 449)',
      alertType: 'positive'
    },
    {
      id: 14367,
      title: 'Τα νέα αρώματα που ξεχωρίζω τώρα: Αυτές είναι οι νέες κυκλοφορίες που μοσχοβολούν άνοιξη',
      image: 'https://www.allyou.gr/images/spring_new_fragrances_part_2_cover.jpg#joomlaImage://local-images/spring_new_fragrances_part_2_cover.jpg',
      domain: 'allyou.gr',
      platform: 'Instagram',
      alertTitle: 'Spike in Clicks',
      alertValue: '+57.14% (7 → 11)',
      alertType: 'positive'
    }
  ];

  private autoplayId: ReturnType<typeof setInterval> | null = null;
  private readonly autoplayDelay = 3500;
  private autoplayPaused = false;

  private updatingToastRef: MatSnackBarRef<any> | null = null;

  private readonly failedStatuses = new Set([
    'Fail Permanently',
    'Failed'
  ]);

  private readonly videoTags = new Set([
    'fbvideo',
    'igvideo',
    'igreel',
    'fbstoryvideo',
    'igstoryvideo',
    'tiktokstoryvideo',
    'tiktokcarousel',
    'tiktokvideo'
  ]);

  constructor(
    private readonly coreService: CoreService,
    private readonly route: ActivatedRoute,
    private readonly router: Router,
    private readonly location: Location,
    private readonly datePipe: DatePipe,
    private readonly snackBar: MatSnackBar
  ) {}

  ngOnInit(): void {
    this.companyId = this.coreService.user?.companyID || 0;

    this.postId =
      this.route.snapshot.paramMap.get('id') ||
      this.route.snapshot.queryParamMap.get('post_id') ||
      this.route.snapshot.queryParamMap.get('id');

    if (!this.companyId) {
      this.setError('Company ID was not found.');
      return;
    }

    if (!this.postId) {
      this.setError('Post ID was not found.');
      return;
    }

    this.loadPost(this.postId);
  }

  ngOnDestroy(): void {
    this.stopAutoplay();
    this.hideUpdatingToast();
  }

  loadPost(postId: string): void {
    this.loading = true;
    this.errorMessage = '';

    this.coreService
      .getData(`/manual-post/editPost/${this.companyId}/${postId}`)
      .pipe(take(1))
      .subscribe({
        next: (res: InstagramEditPostResponse) => {
          if (!res) {
            this.setError('Post not found.');
            this.coreService.showSnackbar('Post not found.');
            return;
          }

          this.mapPostResponse(res);
          this.loading = false;
        },
        error: (error) => {
          console.error('Failed to load Instagram post:', error);
          this.setError('Could not load Instagram photo post preview.');
        }
      });
  }

  goBack(): void {
    this.location.back();
  }

  reusePost(): void {
    if (!this.postId) {
      return;
    }

    this.router.navigate(['/reuse-post', this.postId]);
  }

  goToPreviousPost(): void {
    const previousPostId = this.route.snapshot.queryParamMap.get('previous');

    if (!previousPostId) {
      this.coreService.showSnackbar('Previous post not found.');
      return;
    }

    this.openPostById(previousPostId);
  }

  goToNextPost(): void {
    const nextPostId = this.route.snapshot.queryParamMap.get('next');

    if (!nextPostId) {
      this.coreService.showSnackbar('Next post not found.');
      return;
    }

    this.openPostById(nextPostId);
  }

  openLatestPost(post: any): void {
    if (!post?.id) {
      return;
    }

    this.openPostById(String(post.id));
  }

  private openPostById(postId: string): void {
    this.router.navigate([
      '/manage-preview/instagram-photopost',
      postId
    ]).then(() => {
      this.postId = postId;
      this.loadPost(postId);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    });
  }

  private mapPostResponse(res: InstagramEditPostResponse): void {
    this.siteDomain = this.cleanDomain(res.Domain);
    this.textDirection = res.text_direction === 'rtl' ? 'rtl' : 'ltr';

    this.postStatus = res.post_status || '';
    this.socialTag = res.social_tag || '';

    this.postFeed = res.feed || '';
    this.thumbnailFilePath = res.thumbnail_file_path || '';
    this.postTitle = res.rss_title || '';
    this.postDescription = this.truncateText(res.rss_description || '', 250);
    this.articleUrl = res.article_url || '';
    this.formattedDate = this.formatDate(res.scheduled_for);

    this.resolveMedia(res);
  }

  private resolveMedia(res: InstagramEditPostResponse): void {
    this.resetMediaState();

    const statusOk = !this.failedStatuses.has(this.postStatus);
    const tag = this.socialTag.trim().toLowerCase();

    const rawVideoUrl =
      this.cleanString(res.createomate_video) ||
      this.cleanString(res.video_url);

    const shouldBeVideo = this.isVideoPost(
      this.postStatus,
      this.socialTag,
      rawVideoUrl
    );

    if (statusOk && this.videoTags.has(tag) && !rawVideoUrl) {
      this.showUpdatingToast();
    } else {
      this.hideUpdatingToast();
    }

    if (shouldBeVideo && rawVideoUrl) {
      this.isVideo = true;
      this.videoUrl = this.normalizeStorageUrl(rawVideoUrl);
      this.mainImageUrl = this.cleanString(res.rss_img_url);
      return;
    }

    if (this.isValidCarousel(res)) {
      this.isCarousel = true;
      this.carouselImages = res.image_array || [];
      this.currentIndex = 0;
      this.mainImageUrl = this.carouselImages[0] || '';
      this.startAutoplay();
      return;
    }

    if (res.switchboard_img) {
      this.isSwitchboardImage = true;
      this.mainImageUrl = res.switchboard_img;
      return;
    }

    this.mainImageUrl =
      this.cleanString(res.rss_img_url) ||
      this.firstImageFromArray(res.image_array);
  }

  private resetMediaState(): void {
    this.isVideo = false;
    this.isCarousel = false;
    this.isSwitchboardImage = false;

    this.videoUrl = '';
    this.mainImageUrl = '';
    this.carouselImages = [];
    this.currentIndex = 0;

    this.stopAutoplay();
  }

  private isVideoPost(
    status: string | null | undefined,
    tag: string | null | undefined,
    videoUrl?: string | null | undefined
  ): boolean {
    const safeStatus = (status || '').trim();
    const safeTag = (tag || '').trim().toLowerCase();
    const hasVideoUrl = !!this.cleanString(videoUrl);

    if (this.failedStatuses.has(safeStatus)) {
      return false;
    }

    if (safeTag === 'igcarousel') {
      return false;
    }

    return hasVideoUrl || this.videoTags.has(safeTag);
  }

  private isValidCarousel(res: InstagramEditPostResponse): boolean {
    return (
      this.socialTag.trim().toLowerCase() === 'igcarousel' &&
      !this.failedStatuses.has(this.postStatus) &&
      Array.isArray(res.image_array) &&
      res.image_array.length > 0
    );
  }

  nextSlide(): void {
    if (!this.isCarousel || this.carouselImages.length === 0) {
      return;
    }

    this.currentIndex = (this.currentIndex + 1) % this.carouselImages.length;
  }

  prevSlide(): void {
    if (!this.isCarousel || this.carouselImages.length === 0) {
      return;
    }

    this.currentIndex =
      (this.currentIndex - 1 + this.carouselImages.length) %
      this.carouselImages.length;
  }

  goToSlide(index: number): void {
    if (!this.isCarousel || this.carouselImages.length === 0) {
      return;
    }

    if (index < 0 || index >= this.carouselImages.length) {
      return;
    }

    this.currentIndex = index;
  }

  pauseAutoplay(): void {
    this.autoplayPaused = true;
  }

  resumeAutoplay(): void {
    this.autoplayPaused = false;
  }

  private startAutoplay(): void {
    if (!this.isCarousel || this.autoplayId) {
      return;
    }

    this.autoplayId = setInterval(() => {
      if (!this.isCarousel || this.carouselImages.length === 0) {
        return;
      }

      if (this.autoplayPaused) {
        return;
      }

      this.nextSlide();
    }, this.autoplayDelay);
  }

  private stopAutoplay(): void {
    if (!this.autoplayId) {
      return;
    }

    clearInterval(this.autoplayId);
    this.autoplayId = null;
  }

  private showUpdatingToast(): void {
    if (this.updatingToastRef) {
      return;
    }

    this.updatingToastRef = this.snackBar.open(
      'Updating preview content',
      undefined,
      {
        duration: 0,
        horizontalPosition: 'center',
        verticalPosition: 'bottom'
      }
    );
  }

  private hideUpdatingToast(): void {
    if (!this.updatingToastRef) {
      return;
    }

    this.updatingToastRef.dismiss();
    this.updatingToastRef = null;
  }

  private cleanDomain(domain?: string): string {
    return (domain || 'dummy.view')
      .replace('https://www.', '')
      .replace('http://www.', '')
      .replace('https://', '')
      .replace('http://', '');
  }

  private truncateText(text: string, maxChars: number): string {
    if (!text) {
      return '';
    }

    return text.length > maxChars
      ? `${text.substring(0, maxChars)}...`
      : text;
  }

  private formatDate(date?: string): string {
    if (!date) {
      return '';
    }

    return this.datePipe.transform(new Date(date), 'd MMMM yyyy') || '';
  }

  private firstImageFromArray(images?: string[]): string {
    if (!Array.isArray(images) || images.length === 0) {
      return '';
    }

    return images[0] || '';
  }

  private cleanString(value?: string | null): string {
    return value ? String(value).trim() : '';
  }

  private normalizeStorageUrl(url: string): string {
    if (!url) {
      return '';
    }

    if (/^https?:\/\//i.test(url)) {
      return url;
    }

    const path = url.startsWith('/storage/')
      ? url
      : `/storage/${url.replace(/^\/+/, '')}`;

    const origin = environment.baseUrl.replace(/\/api\/?$/i, '');

    return `${origin}${path}`;
  }

  private setError(message: string): void {
    this.loading = false;
    this.errorMessage = message;
  }
}